import type { ReactNode } from 'react';
import { TenantWorkspaceApi, type OnboardingReadiness, type OnboardingCategoryState } from '@/modules/tenants/services/workspace-api';
import { FiCheckCircle, FiAlertCircle, FiMinusCircle, FiExternalLink } from 'react-icons/fi';
import { renderIcon } from '@/shared/layout/icon-registry';

const CATEGORY_ICONS: Record<OnboardingCategoryState['category'], ReactNode> = {
  business_information: renderIcon('building2', 'h-5 w-5'),
  verticals: renderIcon('package', 'h-5 w-5'),
  contacts: renderIcon('package', 'h-5 w-5'),
  locations: renderIcon('mapPin', 'h-5 w-5'),
  legal_verification: renderIcon('shield', 'h-5 w-5'),
  branding: renderIcon('palette', 'h-5 w-5'),
  website: renderIcon('store', 'h-5 w-5'),
  social: renderIcon('megaphone', 'h-5 w-5'),
  marketing: renderIcon('megaphone', 'h-5 w-5'),
  operating_hours: renderIcon('calendar', 'h-5 w-5'),
  catalog: renderIcon('shoppingBag', 'h-5 w-5'),
  payments: renderIcon('creditCard', 'h-5 w-5'),
  subscription: renderIcon('creditCard', 'h-5 w-5'),
  staff: renderIcon('users', 'h-5 w-5'),
  communications: renderIcon('mail', 'h-5 w-5'),
  policies: renderIcon('fileText', 'h-5 w-5'),
  integrations: renderIcon('link', 'h-5 w-5'),
  media: renderIcon('image', 'h-5 w-5'),
  fulfilment: renderIcon('truck', 'h-5 w-5'),
  visibility: renderIcon('globe', 'h-5 w-5'),
};

const CATEGORY_LABELS: Record<OnboardingCategoryState['category'], string> = {
  business_information: 'Business Information',
  verticals: 'Business Verticals',
  contacts: 'Contact Information',
  locations: 'Locations & Branches',
  legal_verification: 'Legal Verification',
  branding: 'Branding',
  website: 'Website',
  social: 'Social Media',
  marketing: 'Marketing',
  operating_hours: 'Operating Hours',
  catalog: 'Product Catalog',
  payments: 'Payments',
  subscription: 'Subscription',
  staff: 'Staff & Permissions',
  communications: 'Communications',
  policies: 'Policies & Legal',
  integrations: 'Integrations & Webhooks',
  media: 'Media & Assets',
  fulfilment: 'Fulfilment',
  visibility: 'Marketplace Visibility',
};

/**
 * Where each category's 'Configure' button scrolls to.
 *
 * A category this deployment does not support has NO section to configure, and so
 * has NO route: the record is partial on purpose, and a missing entry means the row
 * offers no button rather than one that scrolls nowhere.
 */
const CATEGORY_ROUTES: Partial<Record<OnboardingCategoryState['category'], string>> = {
  business_information: '#business-info',
  verticals: '#verticals',
  contacts: '#business-info',
  locations: '#locations',
  legal_verification: '#business-info',
  branding: '#branding',
  website: '#branding',
  social: '#branding',
  marketing: '#branding',
  operating_hours: '#business-info',
  catalog: '#verticals',
  payments: '#branding',
  subscription: '#branding',
  staff: '#business-info',
  communications: '#business-info',
  policies: '#business-info',
  media: '#branding',
  fulfilment: '#service-areas',
  visibility: '#locations',
};

interface OnboardingChecklistProps {
  readiness: OnboardingReadiness;
}

export function OnboardingChecklist({ readiness }: OnboardingChecklistProps): ReactNode {
  const categories = readiness.categories;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Onboarding Readiness</h2>
          <p className="text-sm text-content-muted mt-1">
            {readiness.overall === 'complete'
              ? 'All supported categories are complete. Your tenant is ready for activation.'
              : `${readiness.missingCount} of ${categories.filter(c => c.status !== 'not_supported').length} supported categories need attention.`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            readiness.overall === 'complete'
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {readiness.overall === 'complete' ? 'Ready' : 'Incomplete'}
          </span>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-3">
        {categories.map((category) => (
          <OnboardingCategoryRow
            key={category.category}
            category={category}
            onNavigate={() => {
              const route = CATEGORY_ROUTES[category.category];
              if (route) {
                const element = document.querySelector(route);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="rounded-lg bg-muted/50 p-4">
        <h4 className="text-sm font-medium mb-3">Status Legend</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <LegendItem icon={<FiCheckCircle className="h-4 w-4 text-green-600" />} label="Complete" />
          <LegendItem icon={<FiAlertCircle className="h-4 w-4 text-yellow-600" />} label="Missing (actionable)" />
          <LegendItem icon={<FiMinusCircle className="h-4 w-4 text-gray-400" />} label="Not Supported (API not available)" />
        </div>
      </div>
    </div>
  );
}

interface OnboardingCategoryRowProps {
  category: OnboardingCategoryState;
  onNavigate: () => void;
}

function OnboardingCategoryRow({ category, onNavigate }: OnboardingCategoryRowProps) {
  const Icon = CATEGORY_ICONS[category.category];
  const label = CATEGORY_LABELS[category.category];
  const hasRoute = category.category in CATEGORY_ROUTES;

  const getStatusConfig = () => {
    switch (category.status) {
      case 'complete':
        return {
          icon: <FiCheckCircle className="h-5 w-5 text-green-600" />,
          bg: 'bg-green-50 border-green-200',
          text: 'text-green-800',
          label: 'Complete',
        };
      case 'missing':
        return {
          icon: <FiAlertCircle className="h-5 w-5 text-yellow-600" />,
          bg: 'bg-yellow-50 border-yellow-200',
          text: 'text-yellow-800',
          label: 'Missing',
        };
      case 'not_supported':
        return {
          icon: <FiMinusCircle className="h-5 w-5 text-gray-400" />,
          bg: 'bg-gray-50 border-gray-200',
          text: 'text-gray-600',
          label: 'Not Supported',
        };
    }
  };

  const status = getStatusConfig();

  return (
    <div className={`rounded-lg border p-4 transition-colors hover:bg-muted/30 ${status.bg}`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-white border border-border">
          {category.category in CATEGORY_ICONS ? CATEGORY_ICONS[category.category] : null}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="font-medium text-content">{label}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.text} bg-white/50 border border-current/20`}>
              {status.label}
            </span>
          </div>

          {category.reason && (
            <p className="mt-1 text-sm text-content-muted">{category.reason}</p>
          )}

          {category.status === 'missing' && hasRoute && (
            <button
              type="button"
              onClick={onNavigate}
              className="mt-2 text-sm text-primary hover:underline flex items-center gap-1"
            >
              Configure
              <FiExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="flex-shrink-0">
          {status.icon}
        </div>
      </div>
    </div>
  );
}

function LegendItem({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-content-muted">{label}</span>
    </div>
  );
}